import { useEffect, useCallback } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'

import { shippingSendMobilePropTypes } from '../../../common/propTypes'
import useEnhancedFormik from '../../../hooks/useEnhancedFormik'
import { useMobileServiceTypes } from '../../../services/user'

export const getOriginInfoForCenter = (centerCode, userCenters) => {
  if (!Boolean(centerCode)) return {}
  const centerData = userCenters.find(el => el.id === centerCode)
  const address = centerData.shippingAddress
  return {
    originPostalCode: address.postalCode,
    originCountryCode: address.country,
  }
}

const useShippingSendServiceTypes = (userCenters, centerCode, destination) => {
  const originInfo = getOriginInfoForCenter(centerCode, userCenters)

  const destinationInfo = destination
    ? {
        destinPostalCode: destination?.postalCode.postalCode,
        destinCountryCode: destination?.country,
      }
    : {}

  const { data: serviceTypesData } = useMobileServiceTypes({
    clientCenterCode: centerCode,
    ...originInfo,
    ...destinationInfo,
  })

  return serviceTypesData?.serviceTypes ?? []
}

const addressYupSchema = Yup.object().shape({
  name: Yup.string().required(),
  address: Yup.string().required(),
  country: Yup.string().required(),
  city: Yup.string().required(),
  phone: Yup.string().required(),
  email: Yup.string(),
  postalCode: Yup.object().shape({
    postalCode: Yup.string().required(),
    city: Yup.string().required(),
  }),
})

const shippingSendMobileSchema = Yup.object().shape({
  centerCode: Yup.string().required(),
  origin: addressYupSchema,
  destination: addressYupSchema,
  serviceType: Yup.object()
    .shape({
      code: Yup.string(),
      name: Yup.string(),
      label: Yup.string(),
      originalCode: Yup.string(),
    })
    .nullable()
    .required(),
  referenceNumber: Yup.string(),
  packagesNumber: Yup.number().nullable().min(1).required(),
  weight: Yup.number().nullable().min(0).required(),
  pickupDay: Yup.string(),
  pickupSchedule: Yup.string(),
  pickupTimezone: Yup.string(),
  pickup: Yup.string(),
  fixedPickup: Yup.bool(),
})

const getDefaultAddressForCenter = (centerCode, userCenters) => {
  const centerData = userCenters.find(el => el.id === centerCode)
  const address = centerData.shippingAddress
  const formattedAddress = {
    ...address,
    postalCode: { postalCode: address.postalCode, city: address.city },
  }
  return formattedAddress
}

const getInitialValues = (defaultCentreCode, userCenters) => ({
  centerCode: defaultCentreCode ?? '',
  origin: getDefaultAddressForCenter(defaultCentreCode, userCenters),
  destination: undefined,
  phone: '',
  email: '',
  referenceNumber: '',
  serviceType: null,
  packagesNumber: null,
  weight: null,
  pickupDay: '',
  pickupSchedule: 'none',
  pickupTimezone: 'none',
  pickup: null,
  fixedPickup: false,
})

const useShippingSendMobileForm = (
  defaultCentreCode,
  userCenters,
  submitForm,
  serverErrors,
  successCallback,
) => {
  const formik = useFormik({
    validationSchema: shippingSendMobileSchema,
    initialValues: getInitialValues(defaultCentreCode, userCenters),
    onSubmit: async values => {
      const shippingCode = await submitForm(values)
      successCallback(shippingCode)
    },
  })

  const enhancedFormik = useEnhancedFormik(
    shippingSendMobilePropTypes,
    formik,
    { errors: serverErrors },
  )

  const serviceTypeOptions = useShippingSendServiceTypes(
    userCenters,
    formik.values.centerCode,
    formik.values.destination,
  )

  //TEMP: para testear el reseteo del campo serviceType si se cambia el centro y no está el tipo disponible
  // const serviceTypeOptions =
  //   formik.values.centerCode === `0000200001`
  //     ? mock_service_types
  //     : [mock_service_types[0]]

  const handleChangeValue = ({ name, value }) =>
    formik.handleChange({ target: { name, value } })

  useEffect(() => {
    const newOriginAddress = getDefaultAddressForCenter(
      formik.values.centerCode,
      userCenters,
    )
    formik.handleChange({
      target: { name: 'origin', value: newOriginAddress },
    })
  }, [formik.values.centerCode, userCenters])

  const resetServiceTypeSelection = useCallback(() => {
    formik.setFieldValue('serviceType', null)
  }, [formik])

  const isSelectedServiceTypeInvalid = useCallback(
    (selectedServiceType, options) =>
      Boolean(
        Array.isArray(options) &&
          options.findIndex(opt => opt.code === selectedServiceType.code) ===
            -1,
      ),
    [],
  )

  useEffect(() => {
    if (
      Boolean(formik.values.serviceType) &&
      isSelectedServiceTypeInvalid(
        formik.values.serviceType,
        serviceTypeOptions,
      )
    ) {
      resetServiceTypeSelection()
    }
  }, [
    formik.values.serviceType,
    serviceTypeOptions,
    resetServiceTypeSelection,
    isSelectedServiceTypeInvalid,
  ])

  useEffect(() => {
    if (serviceTypeOptions.length > 0) {
      formik.setFieldValue('serviceType', serviceTypeOptions[0])
    }
  }, [serviceTypeOptions, formik])

  return {
    serviceTypeOptions,
    values: formik.values,
    touched: formik.touched,
    handleBlur: formik.handleBlur,
    fieldErrors: enhancedFormik.fieldErrors,
    formErrors: enhancedFormik.formErrors,
    handleChange: enhancedFormik.handleChange,
    handleChangeValue,
    handleSubmit: enhancedFormik.handleSubmit,
  }
}

export default useShippingSendMobileForm
